import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { fetchTimeSlots } from '../containers/StoreProfilePage/StoreProfilePage.duck';
import {
    determinePackageType,
    getListingTimeZone,
    checkListingAvailability,
    fetchTimeSlotsForListingDateRange,
} from '../util/listingAvailabilityHelpers';

const MAX_PARALLEL_SLOT_FETCHES = 6;

const runWithConcurrencyLimit = async (taskFactories, limit) => {
    if (!taskFactories.length) return;

    let cursor = 0;
    const workers = Array.from({ length: Math.min(limit, taskFactories.length) }, async () => {
        while (cursor < taskFactories.length) {
            const taskIndex = cursor;
            cursor += 1;
            await taskFactories[taskIndex]();
        }
    });

    await Promise.allSettled(workers);
};

/**
 * Custom hook to manage listing availability for multiple listings
 * Handles fetching time slots and checking availability
 *
 * @param {Array} visibleItems - Currently visible Algolia search hits
 * @param {Object} dateRangeFilter - { startDate, endDate }
 * @param {string} ownerId - Store owner ID to detect store changes
 * @returns {Object} - { availabilityStatus, availabilityLoading }
 */
export const useListingAvailability = (visibleItems, dateRangeFilter, ownerId) => {
    const dispatch = useDispatch();
    const store = useStore();
    const getState = useCallback(() => store.getState(), [store]);

    const [availabilityStatus, setAvailabilityStatus] = useState({});
    const [availabilityLoading, setAvailabilityLoading] = useState({});

    // Track active requests to prevent race conditions
    const activeRequestsRef = useRef(new Set());
    // Track completed request keys so we don't re-fetch when more items load (fast scroll)
    const completedRequestKeysRef = useRef(new Set());
    const debounceTimerRef = useRef(null);
    const lastProcessedRangeRef = useRef(null);
    const lastProcessedItemsRef = useRef(null);
    const lastProcessedOwnerRef = useRef(null);
    const isMountedRef = useRef(true);
    const previousOwnerIdRef = useRef(null);

    // Cleanup function
    const cleanup = useCallback(() => {
        // Clear active requests
        if (activeRequestsRef.current) {
            activeRequestsRef.current.clear();
        }
        if (completedRequestKeysRef.current) {
            completedRequestKeysRef.current.clear();
        }

        // Clear debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    }, []);

    // Detect store changes and reset everything
    useEffect(() => {
        if (previousOwnerIdRef.current !== null &&
            previousOwnerIdRef.current !== ownerId) {
            // Store changed - reset everything
            setAvailabilityStatus({});
            setAvailabilityLoading({});
            cleanup();
            lastProcessedRangeRef.current = null;
            lastProcessedItemsRef.current = null;
            lastProcessedOwnerRef.current = null;
            completedRequestKeysRef.current.clear();
        }
        previousOwnerIdRef.current = ownerId;
    }, [ownerId, cleanup]);

    // Cleanup availability states for items no longer in the list
    useEffect(() => {
        if (!visibleItems || visibleItems.length === 0) {
            setAvailabilityStatus({});
            setAvailabilityLoading({});
            return;
        }

        const currentItemIds = new Set(visibleItems.map(item => item.objectID));

        setAvailabilityStatus(prev => {
            const cleaned = {};
            Object.keys(prev).forEach(listingId => {
                if (currentItemIds.has(listingId)) {
                    cleaned[listingId] = prev[listingId];
                }
            });
            return cleaned;
        });

        setAvailabilityLoading(prev => {
            const cleaned = {};
            Object.keys(prev).forEach(listingId => {
                if (currentItemIds.has(listingId)) {
                    cleaned[listingId] = prev[listingId];
                }
            });
            return cleaned;
        });
    }, [visibleItems]);

    useEffect(() => {
        // Clear any pending debounced calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (!dateRangeFilter?.startDate || !dateRangeFilter?.endDate || !visibleItems?.length) {
            cleanup();
            return cleanup;
        }

        // Debounce rapid date range changes (300ms)
        debounceTimerRef.current = setTimeout(() => {
            const { startDate, endDate } = dateRangeFilter;
            const currentItems = visibleItems; // Capture currently visible items

            // Check if range actually changed (include ownerId to detect store changes)
            const rangeKey = `${startDate.getTime()}-${endDate.getTime()}`;
            const itemsKey = currentItems.map(i => i.objectID).join(',');
            const ownerKey = ownerId || 'unknown';

            if (rangeKey === lastProcessedRangeRef.current &&
                itemsKey === lastProcessedItemsRef.current &&
                ownerKey === lastProcessedOwnerRef.current) {
                return; // Skip if same range, items, and owner
            }

            // When range or owner changes, clear completed keys so we re-fetch for new range
            if (rangeKey !== lastProcessedRangeRef.current || ownerKey !== lastProcessedOwnerRef.current) {
                completedRequestKeysRef.current.clear();
            }

            lastProcessedRangeRef.current = rangeKey;
            lastProcessedItemsRef.current = itemsKey;
            lastProcessedOwnerRef.current = ownerKey;

            // Batch loading state updates
            const loadingUpdates = {};
            const statusUpdates = {};
            const fetchTaskFactories = [];
            const currentItemIds = new Set(currentItems.map(i => i.objectID));

            currentItems.forEach(item => {
                const listingId = item.objectID;
                const timeZone = getListingTimeZone(item);
                const packageType = determinePackageType(startDate, endDate);

                if (!timeZone || !packageType) {
                    // No timezone or package type - mark as unavailable
                    statusUpdates[listingId] = false;
                    loadingUpdates[listingId] = false;
                    return;
                }

                // Create request key to track this specific request (include ownerId for store change detection)
                const requestKey = `${ownerId || 'unknown'}-${listingId}-${startDate.getTime()}-${endDate.getTime()}`;

                // Skip if already fetching or already have a result (prevents duplicate fetches on fast scroll)
                if (activeRequestsRef.current.has(requestKey) || completedRequestKeysRef.current.has(requestKey)) {
                    return;
                }

                activeRequestsRef.current.add(requestKey);
                loadingUpdates[listingId] = true;

                fetchTaskFactories.push(async () => {
                    try {
                        await fetchTimeSlotsForListingDateRange(
                            dispatch,
                            getState,
                            fetchTimeSlots,
                            listingId,
                            startDate,
                            endDate,
                            timeZone
                        );

                        if (!isMountedRef.current || !currentItemIds.has(listingId)) {
                            return;
                        }

                        const state = getState();
                        const isAvailable = checkListingAvailability(
                            listingId,
                            startDate,
                            endDate,
                            timeZone,
                            packageType,
                            state
                        );

                        if (isMountedRef.current) {
                            statusUpdates[listingId] = isAvailable === true;
                            completedRequestKeysRef.current.add(requestKey);
                        }
                    } catch (_error) {
                        if (!isMountedRef.current) {
                            return;
                        }

                        if (currentItemIds.has(listingId)) {
                            statusUpdates[listingId] = false;
                        }
                        completedRequestKeysRef.current.add(requestKey);
                    } finally {
                        if (isMountedRef.current) {
                            loadingUpdates[listingId] = false;
                        }
                        activeRequestsRef.current.delete(requestKey);
                    }
                });
            });

            // Flush initial loading=true states to React NOW, before any async work starts.
            // Without this, setAvailabilityLoading is only called after all tasks finish (in
            // .then()), at which point the finally-blocks have already reset every entry to false
            // — so the UI never sees the loading state and no spinner ever appears.
            if (Object.keys(loadingUpdates).length > 0) {
                setAvailabilityLoading(prev => ({ ...prev, ...loadingUpdates }));
            }

            // Run fetches with concurrency cap; update statuses + clear loading flags when done.
            runWithConcurrencyLimit(fetchTaskFactories, MAX_PARALLEL_SLOT_FETCHES).then(() => {
                // Check again if component is still mounted
                if (!isMountedRef.current) {
                    return;
                }

                // Update statuses in a single batch; don't overwrite existing true with false (race safeguard)
                const hasStatusUpdates = Object.keys(statusUpdates).length > 0;
                if (hasStatusUpdates) {
                    setAvailabilityStatus(prev => {
                        const next = { ...prev };
                        Object.keys(statusUpdates).forEach(listingId => {
                            const value = statusUpdates[listingId];
                            // Never overwrite a known true with false (avoids race when duplicate fetch fails)
                            if (value === true || prev[listingId] !== true) {
                                next[listingId] = value;
                            }
                        });
                        return next;
                    });
                }

                // loadingUpdates entries were reset to false in each task's finally block.
                if (Object.keys(loadingUpdates).length > 0) {
                    setAvailabilityLoading(prev => ({ ...prev, ...loadingUpdates }));
                }
            });
        }, 300);

        // Cleanup function
        return cleanup;
    }, [visibleItems, dateRangeFilter, dispatch, getState, cleanup]);

    // Track mount status and cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            cleanup();
        };
    }, [cleanup]);

    return { availabilityStatus, availabilityLoading };
};

